import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ArrowRightIcon, CheckCheck, Loader2 } from "lucide-react"

import { PaymentDoneLeftPanel } from "@/components/payments/payment-done-left-panel"
import { PaymentDoneRightPanel } from "@/components/payments/payment-done-right-panel"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { usePayment, usePaymentMutations } from "@/hooks/payments/usePayments"
import { useIsMobile } from "@/hooks/useIsMobile"
import { cn } from "@/lib/utils"
import type { Payment } from "@/types/payment"

// The box a payment was for: its rent or its fee names the contract; a rack's or an insurance's names only a line.
function paidContractId(payment: Payment): number | null {
  const item = payment.items.find((entry) => entry.entityType === "rent" || entry.entityType === "rent_fee")
  return item ? Number(item.entityId) : null
}

export function PaymentDonePage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [searchParams] = useSearchParams()
  const paymentId = searchParams.get("paymentId")
  const { invalidatePaymentData } = usePaymentMutations()

  const { data: payment, isLoading } = usePayment(paymentId, false)

  useEffect(() => {
    if (payment?.status === "succeeded") {
      invalidatePaymentData()
    }
  }, [payment?.status, invalidatePaymentData])

  useEffect(() => {
    if (!paymentId) {
      navigate("/payments", { replace: true })
      return
    }

    if (isLoading) {
      return
    }

    if (!payment || payment.status === "pending") {
      navigate(`/payments?paymentId=${paymentId}`, { replace: true })
      return
    }

    if (payment.status === "failed" || payment.status === "canceled") {
      navigate(`/payments?paymentId=${paymentId}`, { replace: true })
    }
  }, [payment, paymentId, isLoading, navigate])

  if (paymentId && isLoading) {
    return (
      <div role="status" aria-label="Загружаем платёж" className="flex min-h-dvh items-center justify-center bg-background">
        <Loader2 className="size-8 animate-spin text-brand" aria-hidden />
      </div>
    )
  }

  if (!paymentId || payment?.status !== "succeeded") {
    return null
  }

  const contractId = paidContractId(payment)

  if (contractId == null) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background p-4 text-center">
        <CheckCheck size={80} className="size-20 text-brand" />
        <h1 className="text-[28px] leading-[36px] font-semibold text-foreground sm:text-[48px] sm:leading-[52px]">
          Оплата прошла успешно
        </h1>
        <UiKitButton type="button" onClick={() => navigate("/home")} endIcon={<ArrowRightIcon />}>
          В личный кабинет
        </UiKitButton>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-background md:flex md:h-dvh md:flex-col">
      <main
        className={cn(
          "mx-auto w-full max-w-[1920px] p-2 sm:p-4 md:flex md:min-h-0 md:grow",
        )}
      >
        <div className="flex w-full flex-col gap-2 md:min-h-0 md:grow md:flex-row">
          <div className="relative shrink-0 rounded-[8px] bg-card md:min-h-0 md:grow md:flex-[42%]">
            <PaymentDoneLeftPanel contractId={contractId} />
          </div>
          <div className="relative shrink-0 rounded-[8px] bg-card md:min-h-0 md:grow md:flex-[58%]">
            <PaymentDoneRightPanel contractId={contractId} />
          </div>
        </div>
      </main>

      {isMobile ? (
        <div className="pb-2 px-2">
          <UiKitButton
            type="button"
            className="w-full justify-between"
            onClick={() => navigate("/home")}
            endIcon={<ArrowRightIcon />}
          >
            В личный кабинет
          </UiKitButton>
        </div>
      ) : null}
    </div>
  )
}
